import type { PageProps } from 'gatsby';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import MainTitle from '@components/MainTitle';
import NotFound from '@components/NotFound';

import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import AppLayout from '@layouts/AppLayout';
import { MainContainer } from '@layouts/DrilldownStyle';

import MainWrapper from '@views/Records/Common/MainWrapper';
import Main from '@views/Uploads/Main';

const messages = defineMessages({
  title: {
    id: 'uploadsMain.title',
    defaultMessage: 'Records/Documents',
  },
});

export default function UploadsPage({ params }: PageProps) {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const {
    state: { isCurator },
  } = useSession();
  const {
    state: { patientView },
  } = useCuration();

  if (isCurator && !patientView) {
    return <NotFound />;
  }

  const component = (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <Main {...params} />
    </ErrorBoundary>
  );

  return isMobile ? (
    <AppLayout friendlyName={formatMessage(messages.title)} showHeader>
      <MainContainer maxWidth="600px">
        <MainTitle title={formatMessage(messages.title)} />
        {component}
      </MainContainer>
    </AppLayout>
  ) : (
    <MainWrapper
      backgroundIcon="files"
      friendlyName={formatMessage(messages.title)}
      loading={false}
      title={formatMessage(messages.title)}
    >
      {component}
    </MainWrapper>
  );
}
